"use client";

import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

/**
 * Client-side boot transcript player. Renders the BIOS, GRUB, kernel
 * ring-buffer, and getty login lines progressively — like a real
 * boot — then hands off to the interactive shell once the prompt
 * appears. Lines fall into three speeds:
 *
 *   • flash groups: clusters of related lines (BIOS, GRUB, kernel
 *     ring, service table) that appear almost simultaneously with
 *     a small jitter between clusters so it still feels like a boot
 *     rather than a wall of text.
 *   • typed lines: the things a human would actually type — the
 *     login username, the password, and the two preset shell
 *     commands. These animate at a fast fixed cadence (~22ms/char)
 *     so they're clearly readable but the whole sequence still
 *     finishes in 1–2s.
 *   • instant lines: blank/separator lines and the getty banner,
 *     which appear at a chosen tick without any animation.
 *
 * Total budget is bounded to ~1800ms so the page feels responsive;
 * the player caps the elapsed time and flushes remaining steps
 * rather than dragging on forever if the tab is throttled.
 */

type BootLine = {
  kind: "bios" | "grub" | "kernel" | "service" | "ok" | "fail" | "info";
  text: string;
};

type Step =
  | { kind: "group"; lines: { text: string; cls: string }[] }
  | { kind: "type"; text: string; cls?: string }
  | { kind: "instant"; text: string; cls?: string; mode?: "newline" | "inline" }
  | { kind: "hold"; ms: number }
  | { kind: "mark" }   // records the current time as the kernel/userspace boundary
  | { kind: "elapsed" };

export type Flushed = {
  /** Ordered segments of text, each with its own class. Keeping
   *  segments separate (rather than concatenating text and
   *  overwriting the class) lets a single visual line hold
   *  multiple colors — e.g. the accent-colored "login: " prompt
   *  followed by the fg-colored typed username. */
  segs: { text: string; cls: string }[];
  /** `newline` (default) — ends with a \n so the next piece of
   *  output starts on a new visual line. `inline` — no trailing
   *  newline; the next piece continues on the same visual line
   *  (used for login prompts like "byemily.org login: " that
   *  sit on the same line as the typed username). */
  mode: "newline" | "inline";
};

type Props = {
  bootLines: BootLine[];
  /** Pre-formatted last-login string (`Tue Aug 19 13:34:13 2026`).
   *  Formatted server-side so this client component doesn't have
   *  to import the server-only `formatLastLogin`. */
  lastLoginText: string;
  /** Called once the boot is fully drawn and the player is done.
   *  The parent then mounts the real interactive terminal with
   *  the prefill already in its history. */
  onDone: () => void;
  /** Called once when the animation finishes, with the final
   *  list of flushed lines. The parent uses this to capture the
   *  boot transcript so it can hand it off to the
   *  InteractiveTerminal as `children` — otherwise the boot
   *  lines would disappear when BootStage unmounts. */
  onFinalLines?: (lines: Flushed[]) => void;
};

function bootLineClass(k: BootLine["kind"]): string {
  switch (k) {
    case "bios":    return "text-[color:var(--color-muted)]";
    case "grub":    return "text-[color:var(--color-accent-soft)]";
    case "kernel":  return "text-[color:var(--color-muted)]";
    case "service": return "text-[color:var(--color-fg)]";
    case "ok":      return "text-[color:var(--color-accent)]";
    case "fail":    return "text-[color:#e06c75]";
    default:        return "text-[color:var(--color-fg)]";
  }
}

const FG = "text-[color:var(--color-fg)]";
const MUTED = "text-[color:var(--color-muted)]";
const ACCENT = "text-[color:var(--color-accent)]";
const GRUB_CLS = "text-[color:var(--color-accent-soft)]";

/** The hardcoded `systemd[1]: Startup finished in ...` line is
 *  filtered out of the boot transcript and re-rendered at the
 *  end with the *actual* elapsed boot time. Matching is done on
 *  the literal prefix so any future tweaks to the line don't
 *  silently break this filter. */
const STARTUP_PREFIX = "systemd[1]: Startup finished in ";

function buildScript(bootLines: BootLine[], lastLoginText: string): Step[] {
  const steps: Step[] = [];

  // BIOS POST block (appears as a tight group)
  steps.push({
    kind: "group",
    lines: [
      { text: "VIA Technologies, Inc. VT8237 BIOS v1.40", cls: MUTED },
      { text: "Copyright (C) 2003-2009 VIA Technologies, Inc.", cls: MUTED },
      { text: "", cls: FG },
      { text: "VIA C7 Esther 1.50GHz                    CPU ID: 6A9", cls: FG },
      { text: "Memory Testing : 2048M", cls: FG },
      { text: "Press DEL to enter SETUP", cls: ACCENT },
      { text: "Detecting IDE drives ...", cls: MUTED },
      { text: "", cls: FG },
    ],
  });
  steps.push({ kind: "hold", ms: 100 });

  // GRUB block
  steps.push({
    kind: "group",
    lines: [
      { text: "GNU GRUB  version 2.06", cls: GRUB_CLS },
      { text: " +--------------------------------------------------------------------------+", cls: MUTED },
      { text: " | > Arch Linux, with Linux linux                                           |", cls: FG },
      { text: " | Arch Linux, with Linux linux (recovery mode)                             |", cls: MUTED },
      { text: " +--------------------------------------------------------------------------+", cls: MUTED },
      { text: "      Use the ^ and v keys to select which entry is highlighted.", cls: GRUB_CLS },
      { text: "      Press enter to boot the selected OS, 'e' to edit the commands", cls: GRUB_CLS },
      { text: "      before booting or 'c' for a command line.", cls: GRUB_CLS },
      { text: "", cls: FG },
    ],
  });
  steps.push({ kind: "hold", ms: 90 });

  // Kernel ring buffer + services: batch the bootLines in chunks of
  // ~3 so they don't all appear at once but still feel fast.
  // Each chunk renders as one group, with a small wait between
  // chunks so the eye can track the progress. The hardcoded
  // systemd startup line is filtered out and re-rendered at the
  // end with the real elapsed boot time.
  //
  // We split the bootLines into "kernel" (everything up to and
  // including `systemd[1]: systemd 258 ...` — the kernel ring
  // buffer, which is what a real kernel prints before userspace
  // starts) and "userspace" (everything after — the systemd
  // service startup lines, which are userspace work). The
  // boundary is recorded by a `mark` step so the final
  // systemd startup line can show realistic separate kernel
  // and userspace times.
  const filtered = bootLines.filter((l) => !l.text.startsWith(STARTUP_PREFIX));
  // The last kernel ring buffer line in the current bootLines
  // is `systemd[1]: systemd 258 ...`; everything from there on
  // is userspace. We find the first non-kernel line and split
  // there.
  const firstUserspaceIdx = filtered.findIndex((l) => l.kind !== "kernel");
  const kernelLines =
    firstUserspaceIdx === -1 ? filtered : filtered.slice(0, firstUserspaceIdx);
  const userspaceLines =
    firstUserspaceIdx === -1 ? [] : filtered.slice(firstUserspaceIdx);

  const chunkSize = 3;
  for (let i = 0; i < kernelLines.length; i += chunkSize) {
    const chunk = kernelLines.slice(i, i + chunkSize);
    steps.push({
      kind: "group",
      lines: chunk.map((l) => ({ text: l.text, cls: bootLineClass(l.kind) })),
    });
    steps.push({ kind: "hold", ms: 50 });
  }
  // Mark the kernel/userspace boundary. The animation loop
  // records `performance.now()` when it hits this step.
  steps.push({ kind: "mark" });
  for (let i = 0; i < userspaceLines.length; i += chunkSize) {
    const chunk = userspaceLines.slice(i, i + chunkSize);
    steps.push({
      kind: "group",
      lines: chunk.map((l) => ({ text: l.text, cls: bootLineClass(l.kind) })),
    });
    steps.push({ kind: "hold", ms: 50 });
  }

  // The systemd startup line is emitted right after the
  // service lines and before the getty banner, with the
  // *actual* elapsed boot time. The animation loop resolves
  // this step by computing kernel and userspace times from
  // the mark boundary.
  steps.push({ kind: "elapsed" });

  // getty banner
  steps.push({ kind: "instant", text: "Arch Linux 6.12.31-arch1-1 (tty1)", cls: FG });
  steps.push({ kind: "instant", text: "", cls: FG });
  steps.push({ kind: "hold", ms: 80 });

  // login prompt + typed username — the prompt is `inline` so
  // the typed username sits on the same visual line. The
  // typed text itself closes the line (mode flips to
  // "newline" when it flushes).
  steps.push({ kind: "instant", text: "byemily.org login: ", cls: ACCENT, mode: "inline" });
  steps.push({ kind: "type", text: "emily", cls: FG });
  steps.push({ kind: "hold", ms: 80 });

  // password prompt + typed asterisks — same trick.
  steps.push({ kind: "instant", text: "Password: ", cls: ACCENT, mode: "inline" });
  steps.push({ kind: "type", text: "********", cls: FG });
  steps.push({ kind: "hold", ms: 80 });

  // last login + hint
  steps.push({
    kind: "instant",
    text: `Last login: ${lastLoginText} from 127.0.0.1`,
    cls: MUTED,
  });
  steps.push({ kind: "instant", text: "", cls: FG });
  steps.push({ kind: "instant", text: '// run "help" for a list of available commands', cls: MUTED });
  steps.push({ kind: "instant", text: "", cls: FG });
  steps.push({ kind: "hold", ms: 40 });
  return steps;
}

const TOTAL_BUDGET_MS = 1800;
const TYPE_INTERVAL_MS = 22;

export function BootSequence({ bootLines, lastLoginText, onDone, onFinalLines }: Props) {
  const script = useMemo(
    () => buildScript(bootLines, lastLoginText),
    [bootLines, lastLoginText],
  );

  // `lines` are the already-flushed lines that get rendered as
  // final text (one entry per row, with className + text).
  // `typed` is the in-progress line currently being typed out
  // (rendered char-by-char).
  const [lines, setLines] = useState<Flushed[]>([]);
  const [typed, setTyped] = useState<{ text: string; cls: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    const startedAt = performance.now();
    // Records `performance.now()` at the kernel→userspace
    // boundary (when the `mark` step fires). The `elapsed`
    // step uses this to split elapsed time into kernel and
    // userspace portions.
    let kernelEndAt: number | null = null;
    // Local accumulator that mirrors `lines` state but doesn't
    // require a render to read. We use this to hand the final
    // transcript to the parent when the animation finishes.
    const local: Flushed[] = [];

    // Sleep helpers. We always re-check the budget after every
    // wait so the player can't drag on past TOTAL_BUDGET_MS if the
    // tab is throttled or the device is slow.
    const wait = (ms: number) =>
      new Promise<void>((resolve) => {
        const remaining = TOTAL_BUDGET_MS - (performance.now() - startedAt);
        if (remaining <= 0) return resolve();
        setTimeout(resolve, Math.min(ms, remaining));
      });

    // Push a flushed entry. If the previous entry is `inline`
    // (no trailing newline — e.g. a login prompt waiting for
    // its answer) AND the new entry also wants to stay on the
    // same line (`mode: "inline"`), the new segment is
    // appended to it. If the new entry is `newline`, the line
    // is closed: the segment is appended but the mode flips
    // to `"newline"` so further entries start fresh.
    const pushFlushed = (entry: Flushed) => {
      const last = local[local.length - 1];
      if (last && last.mode === "inline") {
        local[local.length - 1] = {
          segs: [...last.segs, ...entry.segs],
          mode: entry.mode,
        };
      } else {
        local.push(entry);
      }
    };

    const run = async () => {
      for (const step of script) {
        if (cancelled) return;
        if (performance.now() - startedAt >= TOTAL_BUDGET_MS) break;

        if (step.kind === "group") {
          // Add the whole cluster in one state update so React
          // doesn't tear through N separate paints.
          for (const line of step.lines) {
            local.push({ segs: [{ text: line.text, cls: line.cls }], mode: "newline" });
          }
          setLines([...local]);
        } else if (step.kind === "instant") {
          const entry: Flushed = {
            segs: [{ text: step.text, cls: step.cls ?? FG }],
            mode: step.mode ?? "newline",
          };
          pushFlushed(entry);
          setLines([...local]);
        } else if (step.kind === "type") {
          // Animated typing. We render the typed line as a separate
          // piece of state so the cursor can sit at the end of it
          // while it grows. When done, we flush it into `lines`.
          await new Promise<void>((resolve) => {
            let i = 0;
            const cls = step.cls ?? FG;
            setTyped({ text: "", cls });
            const tick = () => {
              if (cancelled) return resolve();
              if (i >= step.text.length) {
                setTyped(null);
                // Typed text closes the current line. If the
                // previous entry was inline (a prompt like
                // "login: "), the typed text appends to it
                // and flips the mode to newline so the next
                // instant starts a fresh line.
                pushFlushed({ segs: [{ text: step.text, cls }], mode: "newline" });
                setLines([...local]);
                return resolve();
              }
              if (performance.now() - startedAt >= TOTAL_BUDGET_MS) {
                // Budget hit: flush whatever we've got.
                setTyped(null);
                pushFlushed({ segs: [{ text: step.text, cls }], mode: "newline" });
                setLines([...local]);
                return resolve();
              }
              i++;
              setTyped({ text: step.text.slice(0, i), cls });
              setTimeout(tick, TYPE_INTERVAL_MS);
            };
            tick();
          });
        } else if (step.kind === "hold") {
          await wait(step.ms);
        } else if (step.kind === "mark") {
          // Record the kernel→userspace boundary. The
          // `elapsed` step uses this to split the total time
          // into realistic kernel and userspace portions.
          kernelEndAt = performance.now();
        } else if (step.kind === "elapsed") {
          // The systemd startup line, with the *actual* boot
          // time. We split the elapsed time into kernel and
          // userspace portions using the boundary recorded
          // by the `mark` step, then format it the way
          // systemd does: `X.XXXs (kernel) + Y.YYYs (userspace)
          // = Z.ZZZs.`
          const now = performance.now();
          const endAt = kernelEndAt ?? startedAt;
          const kernelSec = Math.max(0, (endAt - startedAt) / 1000);
          const userspaceSec = Math.max(0, (now - endAt) / 1000);
          const totalSec = kernelSec + userspaceSec;
          const fmt = (sec: number) => sec.toFixed(3);
          const line = `systemd[1]: Startup finished in ${fmt(kernelSec)}s (kernel) + ${fmt(userspaceSec)}s (userspace) = ${fmt(totalSec)}s.`;
          pushFlushed({
            segs: [{ text: line, cls: "text-[color:var(--color-fg)]" }],
            mode: "newline",
          });
          setLines([...local]);
        }
      }
      if (cancelled) return;
      // Hand the final transcript to the parent so it can capture
      // the boot lines and pass them to the InteractiveTerminal
      // as `children` when the boot finishes — otherwise the
      // boot lines would disappear when BootStage unmounts.
      onFinalLines?.(local);
      onDone();
    };

    void run();
    return () => {
      cancelled = true;
    };
    // onDone is intentionally omitted — we only want to react to
    // the script identity, and the parent's callback is stable
    // enough for our purposes (re-rendering with a new callback
    // would replay the whole boot).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [script]);

  // Render. We keep the layout identical to a normal terminal
  // transcript: a single column of monospace lines, each ending
  // with \n so the wrapping matches the static version. The
  // typed-in-progress line is rendered separately at the end so
  // its growing cursor is always last. The `mode` flag controls
  // whether the line ends with a newline: `inline` entries sit
  // on the same visual line as whatever follows (used for
  // login prompts), `newline` entries end the line. Each entry
  // holds an array of segments so a single visual line can
  // carry multiple colors (e.g. the accent-colored "login: "
  // prompt followed by the fg-colored typed username).
  const rendered: ReactNode[] = [];
  lines.forEach((line, i) => {
    const trail = line.mode === "inline" ? "" : "\n";
    const isEmpty = line.segs.every((s) => s.text === "");
    if (isEmpty && trail === "") return;
    if (isEmpty) {
      rendered.push(<span key={`l-${i}`}>{"\n"}</span>);
      return;
    }
    rendered.push(
      <span key={`l-${i}`}>
        {line.segs.map((seg, j) => (
          <span key={`s-${i}-${j}`} className={seg.cls}>
            {seg.text}
          </span>
        ))}
        {trail}
      </span>,
    );
  });
  if (typed) {
    // The in-progress typed line continues the current visual
    // line (it's always appended after an inline prompt, or
    // starts its own line for a standalone command). The
    // InteractiveTerminal's normal submit path will add the
    // newline + output, so we just render the growing text
    // here.
    rendered.push(
      <span key="typing" className={typed.cls}>
        {typed.text}
      </span>,
    );
  }

  return <>{rendered}</>;
}